/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useRef, useState } from 'react';
import { Pane, PortalOverlay } from '@leverege/ui-elements';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { capitalize } from '@leverege/string-util';
import S from './StackOverlay.css';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const OVERLAY_TIMEOUT_MS = 300;
const ANIMATIONS = {
  slideFromRight: {
    enter: S.slideFromRightEnter,
    enterActive: S.slideFromRightEnterActive,
    appear: S.slideFromRightEnter,
    appearActive: S.slideFromRightEnterActive,
    exit: S.slideFromRightExit,
    exitActive: S.slideFromRightExitActive
  },
  slideFromLeft: {
    enter: S.slideFromLeftEnter,
    enterActive: S.slideFromLeftEnterActive,
    appear: S.slideFromLeftEnter,
    appearActive: S.slideFromLeftEnterActive,
    exit: S.slideFromLeftExit,
    exitActive: S.slideFromLeftExitActive
  },
  slideFromBottom: {
    enter: S.slideFromBottomEnter,
    enterActive: S.slideFromBottomEnterActive,
    appear: S.slideFromBottomEnter,
    appearActive: S.slideFromBottomEnterActive,
    exit: S.slideFromBottomExit,
    exitActive: S.slideFromBottomExitActive
  },
  slideFromTop: {
    enter: S.slideFromTopEnter,
    enterActive: S.slideFromTopEnterActive,
    appear: S.slideFromTopEnter,
    appearActive: S.slideFromTopEnterActive,
    exit: S.slideFromTopExit,
    exitActive: S.slideFromTopExitActive
  },
  fade: {
    appear: S.fadeEnter,
    appearActive: S.fadeEnterActive,
    enter: S.fadeEnter,
    enterActive: S.fadeEnterActive,
    exit: S.fadeExit,
    exitActive: S.fadeExitActive
  }
};
function DualLayerOverlay({
  stack,
  renderer,
  position,
  animation,
  ...rest
}) {
  const prevStack = useRef(stack);
  const [layers, setLayers] = useState([renderer(stack)]);
  const [action, setAction] = useState();

  // Use dual layer system to allow sliding-to-the-top/sliding-from-the-top animations while rendering, at the most:
  // - 2 components while animations are being performed
  // - 1 component while no animations are being performed

  useEffect(() => {
    if (prevStack.current?.stack?.length < stack.stack.length) {
      setAction('push');
      setLayers([renderer(prevStack.current), renderer(stack)]);
      setTimeout(() => {
        setAction(undefined);
        setLayers([renderer(stack)]);
      }, OVERLAY_TIMEOUT_MS);
    } else if (prevStack.current?.stack?.length > stack.stack.length) {
      setAction('pop');
      setLayers([renderer(stack), renderer(prevStack.current)]);
      setTimeout(() => {
        setAction('popping');
      });
      setTimeout(() => {
        setAction(undefined);
        setLayers([renderer(stack)]);
      }, OVERLAY_TIMEOUT_MS);
    }
    prevStack.current = stack;
  }, [stack.stack]);
  return /*#__PURE__*/_jsx(PortalOverlay, {
    animation: animation,
    position: position,
    ...rest,
    children: /*#__PURE__*/_jsxs(Pane, {
      children: [layers[0], /*#__PURE__*/_jsx(CSSTransition, {
        in: action === 'push' || action === 'pop',
        timeout: 300,
        enter: action === 'push',
        exit: action === 'popping',
        className: classNames(S.sidebar, S[`sidebar${capitalize(position)}`]),
        classNames: ANIMATIONS[animation],
        mountOnEnter: true,
        unmountOnExit: true,
        children: layers[1] || /*#__PURE__*/_jsx(_Fragment, {})
      })]
    })
  });
}
function SingleLayerOverlay({
  stack,
  renderer,
  show: pShow,
  ...rest
}) {
  const [show, setShow] = useState(pShow);
  const [key, setKey] = useState();
  const [layer, setLayer] = useState(/*#__PURE__*/React.cloneElement(renderer(stack)));

  // Clone stateful layer with cloned content in order to be able to freeze it while sliding out due to a stack change.

  useEffect(() => {
    setKey(stack.top()?.name);
    if (pShow === true) {
      setShow(false);
      setTimeout(() => {
        setLayer(/*#__PURE__*/React.cloneElement(renderer(stack)));
        setShow(true);
      }, OVERLAY_TIMEOUT_MS - 100);
    }
  }, [stack.top()?.name]);
  useEffect(() => {
    if (stack.top()?.name === key) {
      setLayer(/*#__PURE__*/React.cloneElement(renderer(stack)));
    }
  }, [stack]);
  useEffect(() => {
    setShow(pShow);
  }, [pShow]);
  return /*#__PURE__*/_jsx(PortalOverlay, {
    show: show,
    ...rest,
    children: layer
  });
}
export default function StackOverlay({
  mode,
  ...rest
}) {
  if (mode === 'dual') {
    return /*#__PURE__*/_jsx(DualLayerOverlay, {
      ...rest
    });
  }
  if (mode === 'single') {
    return /*#__PURE__*/_jsx(SingleLayerOverlay, {
      ...rest
    });
  }
  return null;
}